import { InteractionStatus } from '@azure/msal-browser';
import { useAccount, useMsal } from '@azure/msal-react';
import { GoogleLogin } from '@react-oauth/google';
import { recoverAccount } from 'core/crud/auth.crud';
import LineWithText from 'core/modules/atoms/LineWithText';
import Logo from 'core/modules/atoms/Logo/Logo';
import TextHtml from 'core/modules/atoms/TextHtml';
import ForgotPasswordForm from 'core/modules/components/forms/ForgotPasswordForm/ForgotPasswordForm';
import LoginOraculo from 'core/modules/components/ssoOraculo/LoginOraculo';
import { loginOraculo } from 'core/modules/components/ssoOraculo/serviceOraculo';
import * as coreEntities from 'core/store/index';
import { successResponse } from 'core/utils/utils';
import { PRIVACY, TERMS } from 'edelvives/config/config';
import { defaultConfig } from 'edelvives/config/environment';
import initLayoutConfig from 'edelvives/config/layout/LayoutConfig';
import { getUrlSupport } from 'edelvives/utils/utils';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { withTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { setTimeout } from 'timers';
import { AuthProviderButton, Button, Dialog, InputCheckbox, InputPassword, Loading } from 'ui';
import InputText from 'ui/atoms/InputText/InputText';
import FormField from 'ui/molecules/FormField/FormField';
const POLITICS = { LEGAL: 'legal', PRIVACY: 'privacy', IA: 'ia' };

const FORGOT_PASSWORD_STATE = { INITIAL: 'initial', LOADING: 'loading', SUCCESS: 'success' };

const LoginForm = (props) => {
  const { addToast } = useToasts();
  const { instance, accounts, inProgress } = useMsal();

  const { register, handleSubmit, errors } = useForm();
  const { t, onSubmit, onSSO, loading, showRememberMe, showForgotPassword, onRegister } = props;
  const language = useSelector((state) => coreEntities.i18n.selectors.getLanguage(state));
  const [showPolitics, setShowPolitics] = useState(false);
  const [showForgotPasswordDialog, setShowForgotPasswordDialog] = useState(false);
  const [forgotPasswordState, setForgotPasswordState] = useState(FORGOT_PASSWORD_STATE.INITIAL);
  let lang = language && Object.keys(PRIVACY).includes(language) ? language : 'es';

  const account = useAccount(accounts[0] || {});

  useEffect(() => {
    if (inProgress === InteractionStatus.None && account) {
      // Get access token silently for logged in user
      instance
        .acquireTokenSilent({
          scopes: ['User.Read'],
          account: account,
        })
        .then((response) => {
          if (response && response.accessToken) {
            onSSO(response.accessToken, 'microsoft');
          }
        })
        .catch((error) => {
          console.error('Token acquisition failed:', error);
        });
    }
  }, [inProgress, account]);

  useEffect(() => {
    if (showPolitics === POLITICS.PRIVACY) {
      setTimeout(() => {
        const privacyId = document.getElementById('privacy-policy');
        if (privacyId) {
          privacyId.scrollIntoView({
            behavior: 'smooth',
          });
        }
      }, 100);
    }
  }, [showPolitics]);

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      const interval = setInterval(() => {
        loginOraculo(interval, onSSO);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [inProgress]);

  const onLoginMicrosoft = () => {
    // Check if running on iOS mobile device
    // const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    // if (isIOS) {
    // For iOS devices, use redirect login instead of popup
    instance.loginRedirect({
      scopes: ['User.Read'],
      redirectStartPage: window.location.href,
    });
    // return;
    // }
    // instance.loginPopup({
    //   scopes: ['User.Read'],
    // });
  };

  const onLogin = (data) => {
    if (onSubmit) onSubmit(data.email, data.password);
  };

  const onLoginSSO = (token) => {
    if (onSSO) onSSO(token, 'google');
  };

  const closeShowForgotPasswordDialog = () => {
    setShowForgotPasswordDialog(false);
    setForgotPasswordState(FORGOT_PASSWORD_STATE.INITIAL);
  };

  async function onSendChangePassword(email) {
    setForgotPasswordState(FORGOT_PASSWORD_STATE.LOADING);
    const response = await recoverAccount(email, { link: defaultConfig.BASE_URL_ + 'recover-password?token=[TOKEN]&lang=[LANG]' });
    if (successResponse(response)) {
      setForgotPasswordState(FORGOT_PASSWORD_STATE.SUCCESS);
    } else {
      setForgotPasswordState(FORGOT_PASSWORD_STATE.INITIAL);
      addToast(t(`login:User not exist`), {
        appearance: 'error',
        autoDismiss: true,
      });
    }
  }

  function goToSupport() {
    window.open(getUrlSupport(navigator.language), '_blank');
  }

  function onShowPolitics(selection) {
    const politics = selection === POLITICS.PRIVACY ? PRIVACY[lang] : TERMS[lang];
    if (politics?.endsWith('pdf')) {
      if (selection === POLITICS.PRIVACY) window.open(defaultConfig.BASE_URL_ + 'privacy', '_blank');
      if (selection === POLITICS.LEGAL) window.open(defaultConfig.BASE_URL_ + 'terms', '_blank');
      if (selection === POLITICS.IA) window.open(defaultConfig.BASE_URL_ + 'ia', '_blank');
    } else {
      setShowPolitics(selection);
    }
  }

  return (
    <div className="login-form">
      {forgotPasswordState === FORGOT_PASSWORD_STATE.LOADING && <Loading />}
      <div className="login-form__logo">
        <div className="login-form__item">
          <Logo size="big" />
        </div>
      </div>
      <form onSubmit={handleSubmit(onLogin)} className="login-form__form">
        <div className="login-form__item">
          <h3 className="login-form__title">{t('Log in')}</h3>
        </div>
        <div className="login-form__item">
          <FormField error={!!errors.email} errorMessage={t('common:Required parameter')} label={t('auth:User')}>
            <InputText icon="user" iconLeft={true} placeholder={t('auth:Username')} name="email" referer={register({ required: true })} />
          </FormField>
        </div>
        <div className="login-form__item">
          <FormField error={!!errors.password} errorMessage={t('common:Required parameter')} label={t('auth:Password')}>
            <InputPassword iconLeft={true} placeholder={t('auth:Password')} name="password" referer={register({ required: true })} />
          </FormField>
        </div>
        <div className="login-form__item login-form__item--subactions">
          {showRememberMe && (
            <div className="login-form__checkbox-container">
              <InputCheckbox label={t('Remember me')} />
            </div>
          )}
          {showForgotPassword && (
            <div className="login-form__text login-form__text--forget" onClick={() => setShowForgotPasswordDialog(true)}>
              {t('Forgot your password?')}
            </div>
          )}
        </div>
        <div className="login-form__item">
          <Button testid="btn-login" text={t('Log in')} isLoading={loading} type="submit" isFullWidth />
        </div>
        {
          <>
            <div className="login-form__item">
              <LineWithText text={t('Or')} />
            </div>

            <div className="login-form__third-parties">
              <div className="login-form__item">
                <AuthProviderButton testid="btn-sso-microsoft" provider="microsoft" text={t('login:Login with Microsoft')} onClick={onLoginMicrosoft} />
              </div>

              {initLayoutConfig.login.showOraculo && (
                <div className="login-form__item">
                  <LoginOraculo text={t('login:Login with Polp')} />
                </div>
              )}

              <div className="login-form__item">
                <GoogleLogin
                  width="376px"
                  text="signin_with"
                  onSuccess={(credentialResponse) => {
                    // console.log(getTokenUserDetails(credentialResponse.credential));
                    onLoginSSO(credentialResponse.credential);
                  }}
                  onError={(response) => {
                    console.log('google fail ', response);
                  }}
                  locale={i18next.language}
                />
              </div>
            </div>
          </>
        }
        <div className="login-form__politics">
          <div className="login-form__legal-advice" onClick={() => onShowPolitics(POLITICS.LEGAL)}>
            {t('login:Legal advice')}
          </div>
          <div className="login-form__privacy" onClick={() => onShowPolitics(POLITICS.PRIVACY)}>
            {t('login:Privacy')}
          </div>
          <div className="login-form__ai-politics" onClick={() => onShowPolitics(POLITICS.IA)}>
            {t('login:AI Politics')}
          </div>
          <div className="login-form__support" onClick={() => goToSupport()}>
            {t('common:Technical support')}
          </div>
        </div>
      </form>
      <div className="login-form__signup">
        <span className="login-form__text">
          {t('If you don’t have an account yet,')}

          <span className="login-form__link" onClick={onRegister}>
            {' '}
            {t('register here')}
          </span>
        </span>
      </div>
      <Dialog
        size="xl"
        className="dialog-terms"
        title={t(showPolitics === POLITICS.PRIVACY ? 'login:Privacy' : 'login:Legal advice').toUpperCase()}
        isOpen={showPolitics}
        onClose={() => setShowPolitics(null)}
      >
        <TextHtml text={showPolitics === POLITICS.PRIVACY ? PRIVACY[lang] : TERMS[lang]} />
      </Dialog>
      <Dialog
        size="s"
        className="dialog-forgot-password"
        title={t('recover:recover password')}
        isOpen={showForgotPasswordDialog}
        onClose={closeShowForgotPasswordDialog}
      >
        <ForgotPasswordForm t={t} state={forgotPasswordState} onCancel={closeShowForgotPasswordDialog} onSuccess={onSendChangePassword} />
      </Dialog>
    </div>
  );
};

LoginForm.propTypes = {
  loading: PropTypes.bool,
  onRegister: PropTypes.func,
  onSSO: PropTypes.func,
  onSubmit: PropTypes.func,
  showForgotPassword: PropTypes.bool,
  showRememberMe: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

export default withTranslation(['login', 'auth', 'common', 'recover'])(LoginForm);
